import React, { useMemo, useState } from "react";
import ChapterShell from "@/tutorial/ChapterShell";
import {
  Section,
  Step,
  TipBox,
  InfoBox,
} from "@/tutorial/components/TutorialComponents";
import { Slider } from "@/components/ui/slider";

// Frozen demo values
const DEMO_SALARY = 500;          // UM/month
const DEMO_DAILY = DEMO_SALARY / 30; // ≈ 16.67 UM/day
const DEMO_CONSUMPTION = 3000;    // UM/year
const DEMO_OTHER = 120;           // UM/year (excises)
const DEMO_COTIS = 480;           // UM/year (social charges, frozen)
const DEFAULT_IR = 15;            // %
const DEFAULT_TVA = 20;           // %

function fmt(n) {
  return n.toLocaleString("fr-FR", { maximumFractionDigits: 0 });
}

function WhatIfSimulator() {
  const [ir, setIr] = useState([DEFAULT_IR]);
  const [tva, setTva] = useState([DEFAULT_TVA]);
  const irPct = ir[0];
  const tvaPct = tva[0];

  const stats = useMemo(() => {
    const annualSalary = DEMO_SALARY * 12;
    const irAnnual = annualSalary * (irPct / 100);
    const tvaAnnual = DEMO_CONSUMPTION * (tvaPct / 100);
    const total = irAnnual + DEMO_COTIS + tvaAnnual + DEMO_OTHER;
    const days = total / DEMO_DAILY;

    // Comparison if IR drops by 5 pts
    const altIr = Math.max(5, irPct - 5);
    const altIrAnnual = annualSalary * (altIr / 100);
    const altTotal = altIrAnnual + DEMO_COTIS + tvaAnnual + DEMO_OTHER;
    const altDays = altTotal / DEMO_DAILY;
    const recovered = Math.max(0, days - altDays);
    return { total, days, altIr, altDays, recovered };
  }, [irPct, tvaPct]);

  return (
    <div
      className="rounded-xl border border-brand-gold/40 bg-card p-5 shadow-sm"
      data-testid="chapter-9-simulator"
    >
      <div className="mb-3 text-[11px] font-bold uppercase tracking-widest text-brand-gold">
        Mini-simulateur de démonstration
      </div>

      <div className="space-y-5">
        <div>
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="font-semibold">Taux IR</span>
            <span
              className="font-mono font-bold text-brand-blue dark:text-brand-gold"
              data-testid="chapter-9-ir-value"
            >
              {irPct} %
            </span>
          </div>
          <Slider
            value={ir}
            onValueChange={setIr}
            min={5}
            max={50}
            step={1}
            data-testid="chapter-9-ir-slider"
            aria-label="Taux d'impôt sur le revenu"
          />
          <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
            <span>5 %</span>
            <span>50 %</span>
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="font-semibold">Taux TVA</span>
            <span
              className="font-mono font-bold text-brand-blue dark:text-brand-gold"
              data-testid="chapter-9-tva-value"
            >
              {tvaPct} %
            </span>
          </div>
          <Slider
            value={tva}
            onValueChange={setTva}
            min={5}
            max={30}
            step={1}
            data-testid="chapter-9-tva-slider"
            aria-label="Taux de TVA"
          />
          <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
            <span>5 %</span>
            <span>30 %</span>
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-lg border border-border bg-muted/40 p-4 text-sm">
        <div>
          Avec <strong>IR = {irPct} %</strong> et <strong>TVA = {tvaPct} %</strong> :
        </div>
        <div className="mt-2 space-y-1">
          <div>
            → Prélèvements totaux annuels :{" "}
            <span
              className="font-mono font-bold text-foreground"
              data-testid="chapter-9-total-um"
            >
              {fmt(stats.total)} UM
            </span>
          </div>
          <div>
            → Jours de vie sacrifiés :{" "}
            <span
              className="font-mono font-bold text-brand-blue dark:text-brand-gold"
              data-testid="chapter-9-days"
            >
              {fmt(stats.days)} jours / an
            </span>
          </div>
          <div className="text-muted-foreground">
            → Si l'IR baissait à <strong>{stats.altIr} %</strong>, vous
            récupéreriez{" "}
            <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
              {fmt(stats.recovered)} jours
            </span>{" "}
            par an.
          </div>
        </div>
      </div>

      <p className="mt-3 text-xs italic text-muted-foreground">
        Démonstration figée — salaire 500 UM/mois, consommation 3 000 UM/an.
        Les chiffres réels dépendent de votre profil dans l'application.
      </p>
    </div>
  );
}

const SHORTCUTS = [
  { key: "→ ou Espace", action: "Slide suivante" },
  { key: "←", action: "Slide précédente" },
  { key: "F", action: "Plein écran" },
  { key: "Échap", action: "Quitter la présentation" },
  { key: "1 – 7", action: "Aller directement à la slide N" },
];

// Static SVG line chart for intergenerational debt section.
function DebtCurve() {
  const points = [
    { x: 0, y: 90 },
    { x: 1, y: 80 },
    { x: 2, y: 65 },
    { x: 3, y: 48 },
    { x: 4, y: 30 },
    { x: 5, y: 18 },
    { x: 6, y: 12 },
  ];
  const w = 600;
  const h = 200;
  const px = (x) => 30 + (x / 6) * (w - 50);
  const py = (y) => 20 + (y / 100) * (h - 40);
  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${px(p.x)} ${py(p.y)}`)
    .join(" ");
  return (
    <svg
      role="img"
      aria-label="Courbe d'évolution de la dette en années de vie hypothéquées"
      viewBox={`0 0 ${w} ${h}`}
      className="w-full rounded-md border border-border bg-card p-2"
    >
      {/* gridlines */}
      {[0, 25, 50, 75, 100].map((g) => (
        <line
          key={g}
          x1={px(0)}
          x2={px(6)}
          y1={py(g)}
          y2={py(g)}
          stroke="currentColor"
          strokeOpacity="0.08"
        />
      ))}
      <path d={path} fill="none" stroke="#dc2626" strokeWidth="3" />
      {points.map((p, i) => (
        <circle key={i} cx={px(p.x)} cy={py(p.y)} r="4" fill="#dc2626" />
      ))}
      {/* x labels */}
      {[2026, 2031, 2036, 2041, 2046, 2051, 2056].map((yr, i) => (
        <text
          key={yr}
          x={px(i)}
          y={h - 4}
          textAnchor="middle"
          fontSize="10"
          fill="currentColor"
          opacity="0.7"
        >
          {yr}
        </text>
      ))}
      <text x={10} y={py(0) + 4} fontSize="10" fill="currentColor" opacity="0.7">
        90 ans
      </text>
      <text x={10} y={py(100) - 2} fontSize="10" fill="currentColor" opacity="0.7">
        0 an
      </text>
    </svg>
  );
}

export default function Chapter9() {
  return (
    <ChapterShell id={9}>
      <Section id="section-9-1" eyebrow="9.1" title="Le simulateur « Et si ? »">
        <p>
          Le simulateur permet d'explorer en temps réel l'impact d'un changement
          de fiscalité. Voici une version interactive figée pour la
          démonstration : ajustez les deux curseurs et observez la
          recalculation immédiate.
        </p>
        <WhatIfSimulator />
        <InfoBox title="Lecture politique">
          Une réforme fiscale ne se mesure pas seulement en points de PIB ou en
          recettes : elle se mesure aussi en jours de vie restitués ou
          confisqués aux contribuables.
        </InfoBox>
      </Section>

      <Section
        id="section-9-2"
        eyebrow="9.2"
        title="La dette intergénérationnelle"
      >
        <div className="rounded-xl border-2 border-red-700/60 bg-gradient-to-br from-zinc-900 via-black to-red-950 p-6 text-white shadow-lg">
          <div className="text-[11px] font-bold uppercase tracking-widest text-red-300">
            Manifeste générationnel
          </div>
          <div className="mt-3 font-judicial text-xl font-bold leading-snug sm:text-2xl">
            « Un enfant né en 2026 hérite déjà de plusieurs <span className="text-brand-gold">milliers de jours de sa vie future</span> hypothéqués par la dette publique, sans avoir pu donner son consentement. »
          </div>
        </div>
        <p>
          Le module convertit la dette publique en années de vie préemptées
          sur les enfants à naître, projetées sur 30 ans. La courbe ci-dessous
          montre l'érosion progressive de la « réserve vitale » d'une cohorte
          de naissances 2026, à politique budgétaire constante.
        </p>
        <DebtCurve />
        <InfoBox title="Mode de calcul">
          La dette est multipliée par sa charge annuelle moyenne, divisée par
          le salaire journalier futur projeté (corrigé de l'inflation), et
          rapportée à la cohorte des naissances de l'année.
        </InfoBox>
      </Section>

      <Section
        id="section-9-3"
        eyebrow="9.3"
        title="Comparer deux exercices fiscaux"
      >
        <Step number={1} title="Créer deux projets parallèles">
          Même pays, années fiscales différentes (par exemple 2024 et 2025).
        </Step>
        <Step number={2} title="Sélectionner les deux dans « Mes analyses »">
          Cocher les deux lignes puis cliquer sur le bouton{" "}
          <strong>Comparer</strong>.
        </Step>
        <Step number={3} title="Lire le delta vitaliste">
          Le tableau de comparaison restitue chaque KPI vitaliste avec sa
          variation entre les deux exercices.
        </Step>
        <TipBox title="Bon usage">
          Pour une analyse multi-années, créez un projet par exercice puis
          comparez par paires. La théorie vitaliste exige cette granularité
          pour préserver la signification des seuils.
        </TipBox>
      </Section>

      <Section id="section-9-4" eyebrow="9.4" title="Le mode présentation">
        <p>
          Le mode présentation transforme votre analyse en sept slides
          plein-écran prêts pour conférence ou comité. Tous les raccourcis
          sont disponibles au clavier.
        </p>
        <div className="overflow-x-auto">
          <table
            className="w-full border-collapse text-sm"
            data-testid="chapter-9-shortcuts"
          >
            <thead>
              <tr className="bg-muted/60 text-left">
                <th className="border border-border px-3 py-2 font-semibold">
                  Touche
                </th>
                <th className="border border-border px-3 py-2 font-semibold">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {SHORTCUTS.map((s) => (
                <tr key={s.key}>
                  <td className="border border-border px-3 py-2">
                    <kbd className="rounded bg-card px-2 py-0.5 font-mono text-xs shadow-sm">
                      {s.key}
                    </kbd>
                  </td>
                  <td className="border border-border px-3 py-2">{s.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TipBox title="Lancement rapide">
          Activez le mode présentation depuis le bouton en haut à droite du
          dashboard d'une analyse. Quittez à tout moment avec <kbd>Échap</kbd>.
        </TipBox>
      </Section>
    </ChapterShell>
  );
}
